import { JSX, KeyboardEvent, useEffect, useId } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import messages from './messages';
import {
	Alert,
	Field,
	FieldError,
	FieldInput,
	FieldLabel,
	Fields,
	FieldSelect,
	FinePrint,
	IconSlot,
	Item,
	NoticeText,
	OptionLogomark,
	Panel,
	PanelClip,
	PanelGrid,
	RedirectIcon,
	RedirectNotice,
	RowButton,
	RowDetail,
	SelectChevron,
} from './styles';
import { DisplayedTruemedError, TruemedSelection } from './useTruemedSelection';

const ERROR_MESSAGES: Record<DisplayedTruemedError, MessageDescriptor> = {
	already_subscribed: messages.alreadySubscribed,
	unavailable: messages.unavailable,
	invalid: messages.invalid,
	unknown: messages.unknown,
};

// The row only presents state owned by the host form, whose primary button submits the checkout.
export function TruemedCheckout({
	selection,
	hasPromo = false,
}: {
	selection: TruemedSelection;
	// Truemed charges list price, so a coupon or price promotion on the host form won't apply.
	hasPromo?: boolean;
}): JSX.Element {
	const { formatMessage } = useIntl();
	const panelId = useId();
	const countryId = useId();
	const zipId = useId();
	const zipErrorId = useId();
	const alertId = useId();
	const { isOpen, isStarting, postalCode, showZipError, error, zipInputRef } = selection;

	useEffect(() => {
		if (isOpen) zipInputRef.current?.focus({ preventScroll: true });
	}, [isOpen, zipInputRef]);

	// The host's primary button is disabled until the ZIP is valid, which also blocks the browser's
	// implicit submit on Enter, so an invalid ZIP would give no feedback without handling it here.
	const onZipKeyDown = async (e: KeyboardEvent<HTMLInputElement>): Promise<void> => {
		if (e.key !== 'Enter') return;
		e.preventDefault();
		await selection.start();
	};

	const isServerZipError = error === 'invalid';
	const isZipInvalid = showZipError || isServerZipError;
	const zipDescribedBy = showZipError ? zipErrorId : isServerZipError ? alertId : undefined;

	return (
		<Item>
			<RowButton
				type="button"
				$isOpen={isOpen}
				aria-expanded={isOpen}
				aria-controls={panelId}
				onClick={selection.open}
			>
				<IconSlot>
					<OptionLogomark />
				</IconSlot>
				{formatMessage(messages.rowLabel)}
				<RowDetail>{formatMessage(messages.rowDetail)}</RowDetail>
			</RowButton>
			<PanelGrid id={panelId} $isOpen={isOpen}>
				<PanelClip>
					<Panel>
						<Fields>
							<Field $isFloated>
								<FieldLabel htmlFor={countryId}>{formatMessage(messages.countryLabel)}</FieldLabel>
								<FieldSelect id={countryId} name="country" autoComplete="country" defaultValue="US">
									<option value="US">{formatMessage(messages.countryValue)}</option>
								</FieldSelect>
								<SelectChevron viewBox="0 0 12 8" fill="none" aria-hidden="true" focusable="false">
									<path
										d="M1 1.5l5 5 5-5"
										stroke="currentColor"
										strokeWidth="1.5"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</SelectChevron>
							</Field>
							<Field $isFloated={postalCode !== ''} $isError={isZipInvalid}>
								<FieldLabel htmlFor={zipId}>{formatMessage(messages.zipLabel)}</FieldLabel>
								<FieldInput
									id={zipId}
									name="postalCode"
									type="text"
									inputMode="numeric"
									autoComplete="postal-code"
									value={postalCode}
									onChange={e => selection.changePostalCode(e.currentTarget.value)}
									onBlur={() => postalCode.trim() && !selection.isZipValid && selection.revealZipError()}
									disabled={isStarting}
									ref={zipInputRef}
									onKeyDown={onZipKeyDown}
									aria-invalid={isZipInvalid}
									aria-describedby={zipDescribedBy}
								/>
							</Field>
						</Fields>
						{showZipError && <FieldError id={zipErrorId}>{formatMessage(messages.zipError)}</FieldError>}
						<RedirectNotice>
							<RedirectIcon />
							<NoticeText>{formatMessage(messages.redirectNotice)}</NoticeText>
						</RedirectNotice>
						{hasPromo && <FinePrint>{formatMessage(messages.promoNotApplied)}</FinePrint>}
						{error && (
							<Alert id={alertId} role="alert">
								{formatMessage(ERROR_MESSAGES[error])}
							</Alert>
						)}
					</Panel>
				</PanelClip>
			</PanelGrid>
		</Item>
	);
}
